import * as ActionTypes from "../actions/types";
import { formatApiError } from "../constants/apiErrorMessages";
import {
  mapProductData,
  mapProductComparisonTableDatum,
} from "../constants/map/product";
import { parseApiProducts } from "../constants/parse/product";

const INITIAL_SUB_STATE = {
  subscribeLoading: false,
  subscribeErrorMessage: null,
  subscribedSuccessfully: false,
};

const INITIAL_STATE = {
  apiProductsById: {},
  apiProductsIds: [],
  loading: 0,
  productsFilterByTitle: {},
  category: [],
  categoryFilterById: {},
  categoryFilterIds: [],
  ...INITIAL_SUB_STATE,
};

function fetchApiProduct(state) {
  state.loading = state.loading + 1;
}

function fetchApiProductSuccess(state, action) {
  const { payload } = action;

  // Handle backend DTO directly - no need for payload.data
  const mappedData = mapProductData(payload);

  if (mappedData) {
    // Check if it's a single product fetch
    const fetchedSingleItem = Object.keys(mappedData).length === 1;

    // Merge new products with existing ones
    const newProducts = { ...state.apiProductsById };
    Object.keys(mappedData).forEach((key) => {
      if (
        fetchedSingleItem &&
        newProducts[key] &&
        newProducts[key].subscribed !== undefined
      ) {
        // Preserve subscribed status for single item fetches
        newProducts[key] = {
          ...mappedData[key],
          subscribed: newProducts[key].subscribed,
        };
      } else {
        newProducts[key] = mappedData[key];
      }
    });

    state.apiProductsById = newProducts;
    state.productsFilterByTitle = newProducts;
  }
  state.loading = 0;
}

function fetchComparisonTableSuccess(state, action) {
  const { payload } = action;
  const comparisonTableId = payload.data.id;

  // Find products with this comparison table
  const products = Object.values(state.apiProductsById);
  const matchingProducts = products.filter(
    (product) => product.comparisonTable === comparisonTableId
  );

  if (matchingProducts.length > 0) {
    const comparisonTable = mapProductComparisonTableDatum(
      payload.data,
      payload.included
    );

    matchingProducts.forEach((product) => {
      if (product.uuid) {
        state.apiProductsById[product.uuid] = {
          ...state.apiProductsById[product.uuid],
          comparisonTable: comparisonTable,
        };
      }
    });
  }
}

function subscribeApiProductToApplication(state) {
  state.subscribeErrorMessage = null;
  state.subscribeLoading = true;
}

function subscribeApiProductToApplicationSuccess(state) {
  state.subscribeLoading = false;
  state.subscribedSuccessfully = true;
  state.loading = false;
}

function resetSubscriptionMessage(state) {
  state.subscribeLoading = false;
  state.subscribeErrorMessage = null;
  state.subscribedSuccessfully = false;
}

function subscribeApiProductToApplicationFailed(state, action) {
  // Handle both old and new error formats
  const payload = action.payload;
  let message = "Something went wrong. Please try again";

  // Helper function to extract message from various formats
  const extractMessage = (error) => {
    if (typeof error === "string") {
      return error;
    }
    if (error && typeof error === "object") {
      // Core exception {code, description}: same plain-language wording as the rest of the portal
      if (error.code) {
        return formatApiError(error);
      }
      if (error.description) {
        return error.description;
      }
      // Handle {message} format
      if (error.message) {
        return error.message;
      }
      // Try to stringify if it's a simple object
      if (error.code) {
        return `Error ${error.code}: ${
          error.description || "An error occurred"
        }`;
      }
    }
    return null;
  };

  // New format with exception field
  if (payload.error) {
    message = extractMessage(payload.error) || message;
  } else if (payload.body?.exception) {
    message = extractMessage(payload.body.exception) || message;
  }
  // Old format from response
  else if (payload.response?.body?.message) {
    message = extractMessage(payload.response.body.message) || message;
  } else if (payload.response?.text) {
    try {
      const parsed = JSON.parse(payload.response.text);
      message =
        extractMessage(parsed.message) || extractMessage(parsed) || message;
    } catch (e) {
      // Keep default message
    }
  }

  state.subscribeLoading = false;
  state.subscribeErrorMessage = message;
}

function clearModalState(state) {
  state.subscribeLoading = false;
  state.subscribeErrorMessage = null;
  state.subscribedSuccessfully = false;
}

function clearApiProductsById(state) {
  state.apiProductsById = {};
}

function changeSubscriptionStatusSuccess(state, action) {
  const { type, subscribed, uuid } = action.payload;
  if (type === "api_product" && state.apiProductsById[uuid]) {
    state.apiProductsById[uuid] = {
      ...state.apiProductsById[uuid],
      subscribed: subscribed,
    };
  }
  state.loading = Math.max(0, state.loading - 1);
}

function changeSubscriptionStatusFailed(state) {
  state.loading = Math.max(0, state.loading - 1);
}

function fetchSubscriptionStatus(state) {
  // No-op, just return state as-is
  return state;
}

function fetchApiProductFailed(state) {
  state.loading = Math.max(0, state.loading - 1);
}

function fetchApiProducts(state) {
  state.loading = state.loading + 1;
}

export const apiProduct = {
  initialState: INITIAL_STATE,
  handlers: {
    [ActionTypes.FETCH_API_PRODUCT]: fetchApiProduct,
    [ActionTypes.FETCH_API_PRODUCT_SUCCESS]: fetchApiProductSuccess,
    [ActionTypes.FETCH_API_PRODUCT_FAILED]: fetchApiProductFailed,
    [ActionTypes.SUBSCRIBE_API_PRODUCT_TO_APPLICATION]:
      subscribeApiProductToApplication,
    [ActionTypes.SUBSCRIBE_API_PRODUCT_TO_APPLICATION_SUCCESS]:
      subscribeApiProductToApplicationSuccess,
    [ActionTypes.SUBSCRIBE_API_PRODUCT_TO_APPLICATION_FAILED]:
      subscribeApiProductToApplicationFailed,
    [ActionTypes.UNSUBSCRIBE_FROM_API_PRODUCT]:
      subscribeApiProductToApplication,
    [ActionTypes.UNSUBSCRIBE_FROM_API_PRODUCT_SUCCESS]:
      subscribeApiProductToApplicationSuccess,
    [ActionTypes.UNSUBSCRIBE_FROM_API_PRODUCT_FAILED]:
      subscribeApiProductToApplicationFailed,
    [ActionTypes.CLEAR_SUBSCRIBE_ERROR_MESSAGE]: clearModalState,
    [ActionTypes.CLEAR_API_PRODUCTS_BY_ID]: clearApiProductsById,
    [ActionTypes.RESET_SUBSCRIPTION_MESSAGE]: resetSubscriptionMessage,

    [ActionTypes.CHANGE_SUBSCRIPTION_STATUS_SUCCESS]:
      changeSubscriptionStatusSuccess,
    [ActionTypes.CHANGE_SUBSCRIPTION_STATUS_FAILED]:
      changeSubscriptionStatusFailed,
    [ActionTypes.FETCH_SUBSCRIPTION_STATUS]: fetchSubscriptionStatus,

    [ActionTypes.STORE_API_PRODUCTS]: fetchApiProductSuccess,
    [ActionTypes.FETCH_API_PRODUCTS]: fetchApiProducts,
    [ActionTypes.FETCH_COMPARISON_TABLE_SUCCESS]: fetchComparisonTableSuccess,
  },
};
